import { env } from "@config/env";
import { isNativePlatform } from "@lib/platform";

/** Custom URL scheme registered in iOS Info.plist / Android intent filter. */
export const NATIVE_URL_SCHEME = "komut";

/**
 * Host used for native deep links. Supabase redirect URLs must have a
 * non-empty host (`[scheme]://[host]`), so `https://`-style empty hosts are
 * rejected as invalid URLs. Register `komut://auth/**` in Auth → Redirect URLs.
 */
const NATIVE_URL_HOST = "auth";

/** OAuth redirect URL for Supabase (add this URL in Supabase Auth → Redirect URLs). */
export function getOAuthRedirectUrl(): string {
  if (isNativePlatform()) {
    return `${NATIVE_URL_SCHEME}://${NATIVE_URL_HOST}/callback`;
  }
  return `${getWebAppOrigin()}/auth/callback`;
}

/** Password reset redirect URL (add in Supabase Auth → Redirect URLs). */
export function getPasswordResetRedirectUrl(): string {
  if (isNativePlatform()) {
    return `${NATIVE_URL_SCHEME}://${NATIVE_URL_HOST}/reset-password`;
  }
  return `${getWebAppOrigin()}/auth/reset-password`;
}

function getWebAppOrigin(): string {
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return env.appUrl().replace(/\/$/, "");
}

/** Maps push paths, `komut://…`, or absolute URLs to a React Router path. */
export function parseAppDeepLink(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return "/home";

  // FCM/APNs routing uses app paths like /requests/:id (not valid for `new URL()` alone).
  if (trimmed.startsWith("/")) {
    return trimmed;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol === `${NATIVE_URL_SCHEME}:`) {
      const path = parsed.host
        ? `/${parsed.host}${parsed.pathname}`
        : parsed.pathname || "/";
      return `${path}${parsed.search}`;
    }
    return `${parsed.pathname}${parsed.search}` || "/home";
  } catch {
    return "/home";
  }
}
